import { useGoalById, useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { BreadcrumbDivider, IconFormIcons, Target } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useEntityLinks } from 'shared/hooks/use-entity-links';

import {
  getGoalBreadcrumbTrail,
  GoalBreadcrumbItem,
} from './get-goal-breadcrumb-trail';
import * as Styled from './goal-detail-breadcrumbs.style';

type CrumbIconProps = {
  item: GoalBreadcrumbItem;
};

const CrumbIcon: React.FC<CrumbIconProps> = ({ item }) =>
  item.hasProgress ? (
    <CircleProgress percentage={item.percentage} hidePercentage isSmall />
  ) : (
    <Icon
      icon={item.iconName ? IconFormIcons[item.iconName] ?? Target : Target}
    />
  );

export const GoalDetailBreadcrumbs: React.FC = () => {
  const { id } = useParams();
  const goal = useGoalById(id);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const navigate = useNavigate();
  const moreRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const taskCounts = useTaskCounts();

  const trail = useMemo(
    () =>
      goal
        ? getGoalBreadcrumbTrail(
            goal,
            goals,
            lifeAreas,
            taskCounts?.byGoalProgress,
            links,
          )
        : [],
    [goal, goals, lifeAreas, taskCounts?.byGoalProgress, links],
  );

  if (!goal || !trail.length) {
    return null;
  }

  const current = trail[trail.length - 1];
  const ancestors = trail.slice(0, -1);
  const parent = ancestors[ancestors.length - 1];
  const hasCollapsed = ancestors.length > 1;

  const onNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleOpenMenu = () => setIsOpen(true);
  const handleCloseMenu = () => setIsOpen(false);

  return (
    <Styled.Container>
      {hasCollapsed && (
        <>
          <Styled.MoreButton
            ref={moreRef}
            type="button"
            onClick={handleOpenMenu}
          >
            …
          </Styled.MoreButton>
          <Styled.Separator>
            <BreadcrumbDivider />
          </Styled.Separator>
        </>
      )}

      {!!parent && (
        <>
          <Styled.Crumb>
            <Link to={parent.path}>
              <Styled.IconWrapper>
                <CrumbIcon item={parent} />
              </Styled.IconWrapper>
              <Styled.CrumbName>{parent.name}</Styled.CrumbName>
            </Link>
          </Styled.Crumb>
          <Styled.Separator>
            <BreadcrumbDivider />
          </Styled.Separator>
        </>
      )}

      <Styled.Current title={current.name}>{current.name}</Styled.Current>

      {isOpen && (
        <PopupMenu referenceElement={moreRef} onOutside={handleCloseMenu}>
          <PopupMenuList>
            {trail.map((item, index) => (
              <PopupMenuListItem key={item.id}>
                <PopupMenuButton
                  indentationLevel={index}
                  isSelected={item.isCurrent}
                  disabled={item.isCurrent}
                  overflowEllipsis
                  start={
                    <Styled.IconWrapper>
                      <CrumbIcon item={item} />
                    </Styled.IconWrapper>
                  }
                  onClick={
                    item.isCurrent ? undefined : () => onNavigate(item.path)
                  }
                >
                  {item.name}
                </PopupMenuButton>
              </PopupMenuListItem>
            ))}
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
